<script lang="ts">
    import route from '../Util/routes';
    import { round , createSettingsSubmitHandler } from '@/Util/utils';
    import ToggleButtonSmall from '../Bootstrap/ToggleButtonSmall.svelte'
    import { onMount, tick } from 'svelte';
    import ProfileSettingsInput from './ProfileSettingsInput.svelte';

    export let activity = null;
    export let readonly = false;
    export let title = null;
    export let smallSize = null;
    export let lockable = true;
    export let lockLabel = "Auto-Calc Estimates";
    export let hideTitle = false;
    export let hideLabels = false;
    export let labels = {pp:"Peak", hie:"High", ftp:"Low"}
    export let unitLabels = {pp:"Days", hie:"Days", ftp:"Days"};
    export let hideUnits = false;
    export let hideInputs = false;

//This component is used as base for a variety of profile setting components, not simply training load fields, but also ir-params that have pp, hie, ftp divisions,
//wrappers can be created around this component for specific use cases 

//   $: pp_min_step, hie_min_step, ftp_min_step, setTrainingLoad (profileParams?.starting_training_load)

  export let pp_min_step: number = 0.1;
  export let hie_min_step: number = 0.1;
  export let ftp_min_step: number = 1;
  export let training_load = { pp: pp_min_step, hie: hie_min_step, ftp: ftp_min_step};
  let inputs = {ptl:null, htl:null, ltl:null}
//   export let training_load2 = { pp: pp_min_step, hie: hie_min_step, ftp: ftp_min_step};
  export let auto_calc = false;

  // Whether it has been edited since it was set from the server, or last saved
  export let changed = false;
  let saved = '';
  const snapshot = () => JSON.stringify({ ...training_load, auto_calc });
  // The values as they are now, to send and then mark saved with: an edit made while they are on their way stays to be saved
  export function currentSnapshot() { return snapshot(); }
  // How many times the server's values have been set (setTrainingLoad), to tell whether a response brought its own
  let serverSets = 0;
  export function serverSetCount() { return serverSets; }
  export async function markSaved(sent: string | null = null) {
    if (sent !== null) { saved = sent; return; }
    await tick();   // a bound value may still be on its way
    saved = snapshot();
  }
  $: changed = saved !== '' && (training_load, auto_calc, snapshot()) !== saved;

  // savedAutoCalc: the Auto-Calc a save sent, when its response brings the values: that is what the server has, while the
  // checkbox keeps any change made since
  export function setTrainingLoad(val, savedAutoCalc: boolean | null = null) {
    if(!val || !training_load) return
    training_load.pp = round(val.pp, round(-Math.log10(pp_min_step)));
    if(val?.atc) training_load.hie = round((val.atc /1000), round(-Math.log10(hie_min_step))) ;
    else if (val?.hie) training_load.hie = round(val.hie, round(-Math.log10(hie_min_step)));
    training_load.ftp = round(val.ftp, round(-Math.log10(ftp_min_step)));

    if(val?.auto_calc != null) auto_calc = val?.auto_calc;
    serverSets++;
    if (savedAutoCalc === null) markSaved();
    else markSaved(JSON.stringify({ ...training_load, auto_calc: savedAutoCalc }));
  }
  export function getTrainingLoad(){
    return {...training_load};
  }

  export function setActivity(value){
    activity = value;
  }
 
    export function getBody(){
        return {...training_load, path:activity?.path, auto_calc};
    }

    export function validate(){
        
        if(auto_calc) return true;

        if( training_load.ftp <= 0 ){
            setValidation(inputs.ltl, 'Low Training Load must be greater than 0');
            return false;
        }
        if(training_load.ftp > 500 ){
            setValidation(inputs.ltl, 'Low Training Load must be less than 500');
            return false;
        }
        if( training_load.hie <= 0 ){
            setValidation(inputs.htl, 'High Training Load must be greater than 0');
            return false;
        }
        if(training_load.hie > 60 ){
            setValidation(inputs.htl, 'High Training Load must be less than 60');
            return false;
        }
        if( training_load.pp <= 0 ){
            setValidation(inputs.ptl, 'Peak Training Load must be greater than 0');
            return false;
        }
        if( training_load.pp > 60 ){
            setValidation(inputs.ptl, 'Peak Training Load must be less than 60');
            return false;
        }

        return true;
    }

    function setValidation(input, msg='Invalid Entry'){
        input.setCustomValidity(msg);
        input.reportValidity();
    }

    function clearValidation(input){
        input.setCustomValidity('');
        input.reportValidity();
    }

    function inputsOnInput(event){
        clearValidation(event.currentTarget);
    }

    const postTrainingLoad = route('UserController@postTrainingLoad');
    let _submitHandler = createSettingsSubmitHandler(postTrainingLoad, ()=>{return {tl:getBody()}});
    export async function submitHandler(){ //used to make onSubmit postRequest
        return _submitHandler();
    }


    $:isReadonly = auto_calc && lockable || readonly

    export let entry = null;
    export function setEntry(entry){
        if(!entry) return;
        training_load = entry?.tl;
        activity = {path:entry?.path, date:entry?.start_date, name:entry?.name}
    }
    $:setEntry(entry);
    export function getEditableValues(){
        return {...training_load,};
    }

    onMount(()=>{
        setTrainingLoad(training_load); // handle initial rounding
    })
    
</script>   

<div class="tl-container flex flex-col">
    {#if !hideTitle}
    <div id="subtitle">
        <h4>{title ?? "Training Load"}</h4>
    </div>
    {/if}
    <div class="training-load-form" class:smallSize class:hideLabels class:hideUnits class:hideInputs class:lockable>
        <div class="ftp-div form-group">
            <ProfileSettingsInput bind:el={inputs.ltl} label={labels?.ftp ?? 'Low'} on:input={inputsOnInput} unitLabel={unitLabels.ftp} bind:value={training_load['ftp']} min={ftp_min_step} step={ftp_min_step} color="var(--color-xlss)" disabled={isReadonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class='fa fa-bolt' />
            </ProfileSettingsInput>
        </div>
        <div class="hie-div form-group">
            <ProfileSettingsInput bind:el={inputs.htl} label={labels?.hie ?? 'High'} on:input={inputsOnInput} unitLabel={unitLabels.hie} bind:value={training_load['hie']} min={hie_min_step} step={hie_min_step} color="var(--color-xhss)" disabled={isReadonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class="fa fa-line-chart" />
            </ProfileSettingsInput>
        </div>
        <div class="pp-div form-group">
            <ProfileSettingsInput bind:el={inputs.ptl} label={labels?.pp ?? 'Peak'} on:input={inputsOnInput} unitLabel={unitLabels.pp} bind:value={training_load['pp']} min={pp_min_step} step={pp_min_step} color="var(--color-xpss)" disabled={isReadonly} {hideInputs} {hideLabels} {hideUnits}>
                <i class="fa fa-bolt" />
            </ProfileSettingsInput>
        </div>
    </div>
    
    <div style={readonly||!lockable?"display:none;":""}>
        <div style={hideLabels?"":"margin-top:20px;"}>
            <ToggleButtonSmall bind:isChecked={auto_calc} label={lockLabel}></ToggleButtonSmall>
        </div>
    </div>
</div>



<style lang="scss">

.form-group{
    flex: 1 1 150px;
    margin: 0;
}

.tl-container{
    container-name: tl-container;
    container-type: inline-size;
    width: 100%;
    height: 100%;

}
.training-load-form{
    display:grid;
    grid-template-columns: repeat(12, 1fr);
    grid-template-rows: repeat(12, 1fr);
    height: 100%;
    align-items: center;
}

@container tl-container (max-width: 300px) {

    .training-load-form{
        row-gap: 12px;
        .ftp-div{
            grid-row: 9 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
        .hie-div{
            grid-row: 5 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
        .pp-div{
            grid-row: 1 /span 4 !important;
            grid-column: 1 / span 12 !important;
        }
    }
}

@container tl-container (min-width: 300px) {
    .training-load-form{
        >div{
            margin: 0 4px;
        }
        .ftp-div{
            grid-column: 1 /span 4;
            grid-row: 1 / span 12;
        }
        .hie-div{
            grid-column: 5 /span 4;
            grid-row: 1 / span 12;
        }
        .pp-div{
            grid-column: 9 /span 4;
            grid-row: 1 / span 12;
        }
    }
}


</style>
